import { useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { adminApi, type AdminCompany } from "@/lib/api";
import { useDataTable } from "@/hooks/use-data-table";
import { DataTable } from "@/components/ui";
import { Button } from "@/components/ui/button";
import type { ColumnDef, RowAction, BulkAction } from "@/components/ui";
import { Trash2, RotateCcw, Pencil, Download, Clock } from "lucide-react";
import { AdminCompanyDetailModal } from "./AdminCompanyDetailModal";
import { downloadCsv } from "@/lib/utils";
import { PageMeta, ADMIN_PAGE_META } from "@/lib/seo";

const TYPE_OPTIONS = [
  { value: "", label: "Alla typer" },
  { value: "AB", label: "AB" },
  { value: "HB", label: "HB" },
  { value: "KB", label: "KB" },
  { value: "EF", label: "EF" },
];

const ACTIVATION_STATUS_OPTIONS = [
  { value: "", label: "Alla" },
  { value: "active_subscription", label: "Aktiv" },
  { value: "canceled_subscription", label: "Avslutas" },
  { value: "past_due", label: "Förfallen" },
  { value: "inactive", label: "Ej aktiverat" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Aktiva" },
  { value: "all", label: "Alla" },
];

const typeBadgeStyles: Record<string, string> = {
  AB: "bg-blue-100 text-blue-700",
  HB: "bg-purple-100 text-purple-700",
  KB: "bg-purple-100 text-purple-700",
  EF: "bg-green-100 text-green-700",
};

const activationStatusLabels: Record<string, string> = {
  active_subscription: "Aktiv",
  canceled_subscription: "Avslutas",
  past_due: "Förfallen",
  inactive: "Ej aktiverat",
};

const activationStatusStyles: Record<string, string> = {
  active_subscription: "bg-green-100 text-green-700",
  canceled_subscription: "bg-yellow-100 text-yellow-700",
  past_due: "bg-red-100 text-red-700",
  inactive: "bg-gray-100 text-gray-500",
};

// Default retention period (can be changed in admin settings)
const DEFAULT_RETENTION_DAYS = 30;

/**
 * Calculate days remaining until permanent deletion
 */
function getDaysUntilPermanentDelete(deletedAt: string | null | undefined): number | null {
  if (!deletedAt) return null;
  const deletedDate = new Date(deletedAt);
  const permanentDeleteDate = new Date(deletedDate);
  permanentDeleteDate.setDate(permanentDeleteDate.getDate() + DEFAULT_RETENTION_DAYS);
  const now = new Date();
  const diffMs = permanentDeleteDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

export function AdminCompanies() {
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const {
    data,
    isLoading,
    search,
    setSearch,
    filters,
    setFilter,
    pagination,
    setPage,
    setPageSize,
    refresh,
    sortColumnId,
    sortDirection,
    handleSort,
    resetFilters,
  } = useDataTable<AdminCompany, { status: string; type: string; city: string; activationStatus: string; createdAt: string }>({
    fetcher: (params) =>
      adminApi.listCompanies({
        page: params.page,
        pageSize: params.pageSize,
        search: params.search,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        includeDeleted: params.status === "all" || undefined,
        type: params.type || undefined,
        city: params.city || undefined,
        activationStatus: params.activationStatus || undefined,
        createdAfter: params.createdAt || undefined,
        createdBefore: params.createdAt || undefined,
      }),
    pageSize: 25,
    initialFilters: { status: "active", type: "", city: "", activationStatus: "", createdAt: "" },
    sortKeyMap: {
      name: "name",
      type: "type",
      city: "city",
      createdAt: "createdAt",
    },
  });

  const handleDelete = useCallback(
    async (company: AdminCompany) => {
      if (!confirm("Är du säker på att du vill ta bort detta företag?")) return;
      try {
        const response = await adminApi.deleteCompany(company.id);
        if (response.success) {
          refresh();
          toast.success("Företaget har tagits bort");
        }
      } catch {
        toast.error("Kunde inte ta bort företaget");
      }
    },
    [refresh]
  );

  const handleRestore = useCallback(
    async (company: AdminCompany) => {
      try {
        const response = await adminApi.restoreCompany(company.id);
        if (response.success) {
          refresh();
          toast.success("Företaget har återställts");
        }
      } catch {
        toast.error("Kunde inte återställa företaget");
      }
    },
    [refresh]
  );

  const columns: ColumnDef<AdminCompany>[] = useMemo(
    () => [
      {
        id: "name",
        header: "Företag",
        sortable: true,
        sortAccessor: (row) => row.name,
        filter: {
          type: "text" as const,
          value: search,
          onChange: setSearch,
          placeholder: "Sök namn eller org.nr...",
        },
        cell: (row) => (
          <div>
            <p className="text-sm font-medium text-gray-900">{row.name}</p>
            <p className="text-xs text-gray-500">{row.organizationNumber}</p>
          </div>
        ),
      },
      {
        id: "type",
        header: "Typ",
        hideBelow: "md",
        sortable: true,
        sortAccessor: (row) => row.type,
        filter: {
          type: "select" as const,
          value: filters.type,
          onChange: (v: string) => setFilter("type", v),
          options: TYPE_OPTIONS,
        },
        cell: (row) => (
          <span
            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${typeBadgeStyles[row.type] || "bg-gray-100 text-gray-700"}`}
          >
            {row.type}
          </span>
        ),
      },
      {
        id: "city",
        header: "Ort",
        hideBelow: "md",
        sortable: true,
        sortAccessor: (row) => row.city || "",
        filter: {
          type: "text" as const,
          value: filters.city,
          onChange: (v: string) => setFilter("city", v),
          placeholder: "Sök ort...",
        },
        cell: (row) => (
          <span className="text-sm text-gray-500">
            {row.city || "-"}
          </span>
        ),
      },
      {
        id: "activationStatus",
        header: "Prenumeration",
        hideBelow: "md",
        sortable: true,
        sortAccessor: (row) => row.activationStatus || "inactive",
        filter: {
          type: "select" as const,
          value: filters.activationStatus,
          onChange: (v: string) => setFilter("activationStatus", v),
          options: ACTIVATION_STATUS_OPTIONS,
        },
        cell: (row) => {
          const status = row.activationStatus || "inactive";
          return (
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${activationStatusStyles[status] || "bg-gray-100 text-gray-500"}`}
            >
              {activationStatusLabels[status] || status}
            </span>
          );
        },
      },
      {
        id: "createdAt",
        header: "Skapad",
        hideBelow: "lg",
        sortable: true,
        sortAccessor: (row) => new Date(row.createdAt),
        filter: {
          type: "date" as const,
          value: filters.createdAt,
          onChange: (v: string) => setFilter("createdAt", v),
        },
        cell: (row) => (
          <span className="text-sm text-gray-500">
            {new Date(row.createdAt).toLocaleDateString("sv-SE")}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        hideBelow: "lg",
        sortable: true,
        sortAccessor: (row) => (row.deletedAt ? 1 : 0),
        filter: {
          type: "select" as const,
          value: filters.status,
          onChange: (v: string) => setFilter("status", v),
          options: STATUS_OPTIONS,
        },
        cell: (row) => {
          if (row.deletedAt) {
            const daysRemaining = getDaysUntilPermanentDelete(row.deletedAt);
            return (
              <div className="flex flex-col gap-1">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  <Trash2 size={12} />
                  Borttagen
                </span>
                {daysRemaining !== null && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-red-600">
                    <Clock size={10} />
                    {daysRemaining === 0
                      ? "Raderas snart"
                      : daysRemaining === 1
                        ? "Raderas om 1 dag"
                        : `Raderas om ${daysRemaining} dagar`}
                  </span>
                )}
              </div>
            );
          }
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              Aktiv
            </span>
          );
        },
      },
    ],
    [search, setSearch, filters.type, filters.city, filters.activationStatus, filters.createdAt, filters.status, setFilter]
  );

  const rowActions: RowAction<AdminCompany>[] = useMemo(
    () => [
      {
        key: "edit",
        label: "Redigera",
        icon: Pencil,
        onClick: (row) => setSelectedCompanyId(row.id),
      },
      {
        key: "restore",
        label: "Återställ",
        icon: RotateCcw,
        variant: "success",
        onClick: handleRestore,
        visible: (row) => !!row.deletedAt,
      },
      {
        key: "delete",
        label: "Ta bort",
        icon: Trash2,
        variant: "danger",
        onClick: handleDelete,
        visible: (row) => !row.deletedAt,
      },
    ],
    [handleDelete, handleRestore]
  );

  // Bulk actions
  const handleBulkDelete = useCallback(
    async (companies: AdminCompany[]) => {
      const deletableCompanies = companies.filter((c) => !c.deletedAt);
      if (deletableCompanies.length === 0) {
        toast.error("Inga företag kan tas bort");
        return;
      }
      if (
        !confirm(
          `Är du säker på att du vill ta bort ${deletableCompanies.length} företag?`
        )
      ) {
        return;
      }
      setIsBulkDeleting(true);
      try {
        let successCount = 0;
        let errorCount = 0;
        for (const company of deletableCompanies) {
          try {
            const response = await adminApi.deleteCompany(company.id);
            if (response.success) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch {
            errorCount++;
          }
        }
        if (successCount > 0) {
          toast.success(`${successCount} företag har tagits bort`);
          setSelectedKeys(new Set());
          refresh();
        }
        if (errorCount > 0) {
          toast.error(`Kunde inte ta bort ${errorCount} företag`);
        }
      } finally {
        setIsBulkDeleting(false);
      }
    },
    [refresh]
  );

  const handleBulkExport = useCallback((companies: AdminCompany[]) => {
    downloadCsv(
      companies,
      [
        { header: "ID", accessor: (r) => r.id },
        { header: "Namn", accessor: (r) => r.name },
        { header: "Org.nummer", accessor: (r) => r.organizationNumber },
        { header: "Typ", accessor: (r) => r.type },
        { header: "Ort", accessor: (r) => r.city },
        { header: "Prenumeration", accessor: (r) => activationStatusLabels[r.activationStatus || "inactive"] || r.activationStatus },
        { header: "Skapad", accessor: (r) => new Date(r.createdAt).toLocaleDateString("sv-SE") },
        { header: "Status", accessor: (r) => (r.deletedAt ? "Borttagen" : "Aktiv") },
      ],
      `foretag-urval-${new Date().toISOString().slice(0, 10)}.csv`
    );
    toast.success(`${companies.length} företag exporterade`);
  }, []);

  const bulkActions: BulkAction<AdminCompany>[] = useMemo(
    () => [
      {
        key: "export",
        label: "Exportera",
        icon: Download,
        onClick: handleBulkExport,
      },
      {
        key: "delete",
        label: "Ta bort",
        icon: Trash2,
        variant: "danger",
        onClick: handleBulkDelete,
        loading: isBulkDeleting,
      },
    ],
    [handleBulkExport, handleBulkDelete, isBulkDeleting]
  );

  return (
    <>
      <PageMeta title={ADMIN_PAGE_META.companies.title} noIndex />
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Företag</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Hantera alla registrerade företag</p>
          </div>
        <Button
          className="w-full sm:w-auto"
          variant="outline"
          size="sm"
          onClick={() =>
            downloadCsv(
              data,
              [
                { header: "ID", accessor: (r) => r.id },
                { header: "Namn", accessor: (r) => r.name },
                { header: "Org.nummer", accessor: (r) => r.organizationNumber },
                { header: "Typ", accessor: (r) => r.type },
                { header: "Ort", accessor: (r) => r.city },
                { header: "Skapad", accessor: (r) => new Date(r.createdAt).toLocaleDateString("sv-SE") },
                { header: "Status", accessor: (r) => (r.deletedAt ? "Borttagen" : "Aktiv") },
              ],
              `foretag-${new Date().toISOString().slice(0, 10)}.csv`
            )
          }
          disabled={data.length === 0}
        >
          <Download size={14} />
          Exportera CSV
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={data}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        emptyMessage="Inga företag hittades"
        pagination={pagination}
        onPageChange={setPage}
        pageSizeOptions={[25, 50, 100, 250]}
        onPageSizeChange={setPageSize}
        onRowClick={(row) => setSelectedCompanyId(row.id)}
        rowActions={rowActions}
        rowClassName={(row) => (row.deletedAt ? "bg-red-50/50 hover:bg-red-100/50" : undefined)}
        serverSortColumnId={sortColumnId}
        serverSortDirection={sortDirection}
        onSort={handleSort}
        onResetFilters={resetFilters}
        selectable
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
        bulkActions={bulkActions}
      />

      {selectedCompanyId && (
        <AdminCompanyDetailModal
          companyId={selectedCompanyId}
          onClose={() => setSelectedCompanyId(null)}
          onUpdated={refresh}
        />
      )}
    </div>
    </>
  );
}
